// Creature skins on the client (rules in shared/skins.ts): which sprite id a
// creature draws with, the Skins section of the diamond shop, and the admin
// season switch. The server checks ownership; this only draws and sends.
//
// Drawing: pass a creature through myArt / artOf / creatureArt and hand the
// result to the normal sprite helpers in assets.ts (spriteUrl, creatureIcon,
// sheet, companionUrl). The skin's sprite id `<species>~<skin>` is used only
// when creatures/manifest.json lists the skin on the species (assets hasSkin)
// and the creature is not an Aura; otherwise the species id comes back and the
// normal (or Aura) look is drawn.
import { SKINS, SKIN, SKIN_SEASONS, ownsSkin, skinOf, skinArt, type SkinDef, type SkinSeason } from '../shared/skins.ts';
import { SPECIES } from '../shared/species.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import { S } from './state.ts';
import { hasSkin, creatureIcon, iconUrl } from './assets.ts';
import { esc } from './fmt.ts';
import { confirmClick, confirmLabel, priceLabel, isArmed } from './confirm.ts';

/** Sprite id for one of my own creatures of this species (my skin when I use one). */
export function myArt(species: string, aura = false): string {
  return skinArt(species, skinOf(S.trainer ?? undefined, species), aura, hasSkin);
}

/** Sprite id for someone's creature as the server described it (`skin` set when its owner uses one). */
export function artOf(species: string, skin: string | undefined, aura = false): string {
  return skinArt(species, skin, aura, hasSkin);
}

/**
 * Sprite id for a creature that may be mine or someone else's: a `skin` field from the server wins; a creature in
 * my own collection uses my skins; anything else draws its species.
 */
export function creatureArt(c: { species: string; skin?: string; id?: number; aura?: boolean; rolls?: { aura: boolean } }): string {
  const aura = !!(c.aura || c.rolls?.aura);
  if (c.skin) return artOf(c.species, c.skin, aura);
  const mine = c.id !== undefined ? S.creatures.get(c.id) : undefined;
  return mine && mine.species === c.species && !mine.listed ? myArt(c.species, aura) : c.species;
}

const seasonName = (s: SkinSeason) => SKIN_SEASONS[s]?.name ?? s;

function card(s: SkinDef) {
  const t = S.trainer!;
  const own = ownsSkin(t, s.id);
  const on = own && (t.skinsOn ?? []).includes(s.id);
  const drawn = artOf(s.species, s.id) === s.id;
  const sp = SPECIES[s.species];
  const key = `skin:${s.id}`;
  const pills = [on ? '<span class="pill lead">In use</span>' : own ? '<span class="pill good">Owned</span>' : '', `<span class="pill">${esc(sp?.name ?? s.species)}</span>`, drawn ? '' : '<span class="pill">Art soon</span>'].join('');
  const btn = own
    ? `<button class="btn small${on ? '' : ' primary'}" data-act="skin-use" data-id="${s.id}" aria-pressed="${on}">${on ? 'Stop using' : 'Use skin'}</button>`
    : `<button class="btn small gold${isArmed(key) ? ' armed' : ''}" data-act="skin-buy" data-id="${s.id}"${t.diamonds < s.price ? ' disabled title="Not enough diamonds"' : ''}>${priceLabel(key, `Buy · ${s.price} ◆`, s.price)}</button>`;
  return `<div class="ofcard skin-card${on ? ' on' : ''}${own ? '' : ' locked'}" data-key="sk-${s.id}">
    <div class="of-pic skin-pic">${creatureIcon(drawn ? s.id : s.species, false, esc(s.name))}<span class="skin-ribbon ${s.season}">${esc(seasonName(s.season))}</span></div>
    <div class="grow"><div class="row between"><span><b class="nm">${esc(s.name)}</b> ${pills}</span></div>
      <p>${esc(s.look)}.</p>
      <div class="buyrow">${btn}</div></div></div>`;
}

/** The Skins section of the diamond shop: skins on sale, plus any you own from a closed season. */
export function shopSkins(): string {
  const t = S.trainer;
  if (!t) return '';
  const open = new Set(t.skinSeasons ?? []);
  const list = SKINS.filter((s) => open.has(s.season) || ownsSkin(t, s.id));
  if (!list.length) return '';
  return `<div class="ofgrid skin-grid" id="shop-skins" data-fit="ds-skins">${list.map(card).join('')}</div>`;
}

/** Admin: open or close each skin season in the shop. */
export function skinsAdmin(): string {
  const open = new Set(S.trainer?.skinSeasons ?? []);
  return `<h4 class="adm-h">Skins</h4>
    <div class="actions">${(Object.keys(SKIN_SEASONS) as SkinSeason[]).filter((s) => !SKIN_SEASONS[s].passOnly).map((s) => `<span class="muted">${esc(seasonName(s))}: ${open.has(s) ? 'on sale' : 'off sale'}</span><button class="btn small" data-act="skin-adm" data-id="${s}" data-to="${open.has(s) ? '0' : '1'}">${open.has(s) ? `End the ${esc(seasonName(s))} window` : `Reopen ${esc(seasonName(s))}`}</button>`).join('')}<span class="muted">Owners keep their skins either way.</span></div>`;
}

/** Clicks in the Skins section and the admin switch; true when handled. */
export function skinsClick(el: HTMLElement, send: (m: ClientMsg) => void): boolean {
  const id = el.dataset.id ?? '';
  switch (el.dataset.act) {
    // Diamond purchases take a second click on the same button (inline, no dialog).
    case 'skin-buy': if (SKIN[id] && confirmClick(`skin:${id}`)) send({ t: 'skinBuy', id }); return true;
    case 'skin-use': if (SKIN[id]) send({ t: 'skinUse', id, on: !(S.trainer?.skinsOn ?? []).includes(id) }); return true;
    case 'skin-adm': send({ t: 'admSkinWindow', season: id, open: el.dataset.to === '1' }); return true;
    default: return false;
  }
}
